// The account panel's Company info tab for an AE (20 Oct). The panel itself (header, fact strip,
// Scout, Research and History tabs) is the same for every role; only this tab is the role's own,
// chosen in `lib/roleViews.ts`.
//
// The header strip above every tab already carries the lead deal's stage, amount and close date,
// the next meeting, the SDR owner and usage (`aeFactStrip`), so this tab does not repeat them. It
// holds the suggested next steps (`aeProposals`), the open opportunities in full (next step, who
// it is with), the last touch from each side and the latest touches, usage by org, and past deals. Nothing of the strategist's: no why-now,
// research summary, outreach, bank or routing. Read-only, like the rest of the AE view.

import type { BookAccount, Opportunity } from '../api/types';
import { contactDisplayName } from '../lib/contactName';
import { formatMoney } from '../lib/format';
import { lastOutbound } from '../book/derived';
import { OrgRow, TouchRow } from '../sidebar/HistoryTab';
import { dealOutcome, orgTrends, pastDeals, touchTimeline } from '../sidebar/historyData';
import { outreachPeople } from '../sidebar/outreachPeople';
import { openOpportunities } from '../sidebar/panelFacts';
import { PanelSection } from '../sidebar/panelParts';
import { aeProposals, dealAmount, relativeDay } from './aeFacts';
import { leadDeal } from './aeSections';

// How many of the latest touches the tab lists. The full timeline is on History.
const RECENT_TOUCHES = 6;

function dayWithAge(day: string | null | undefined, today: string, empty: string): string {
  if (!day) return empty;
  return `${day} (${relativeDay(day, today)})`;
}

// One open deal, every field on its own line so nothing is cut.
function DealCard({ deal, today }: { deal: Opportunity; today: string }) {
  const amount = dealAmount(deal);
  const amountNote = !deal.amount && deal.qualified_pipeline ? ' (qualified pipeline)' : '';
  const contact = deal.contact;
  return (
    <div className="aedeal">
      <div className="aedealname">{deal.name || 'Unnamed opportunity'}</div>
      <dl>
        <dt>Stage</dt><dd>{deal.stage || 'No stage'}</dd>
        <dt>Amount</dt><dd>{amount == null ? '—' : formatMoney(amount) + amountNote}</dd>
        <dt>Close date</dt><dd>{dayWithAge(deal.close_date, today, 'Not set')}</dd>
        <dt>Next step</dt><dd>{deal.next_step || 'None written'}</dd>
        {contact && (contact.name || contact.email) ? (
          <>
            <dt>With</dt>
            <dd>
              {contactDisplayName(contact.name, contact.email, '')}
              {contact.title ? <span className="muted"> · {contact.title}</span> : null}
              {contact.source === 'activity' ? <span className="muted"> (from activity)</span> : null}
            </dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

export function AeCompanyInfo({ row, today }: { row: BookAccount; today: string }) {
  const open = openOpportunities(row);
  const lead = leadDeal(row);
  const proposals = aeProposals(row, today);
  const touches = touchTimeline(row).slice(0, RECENT_TOUCHES);
  const trends = orgTrends(row, outreachPeople(row));
  const lastSdr = lastOutbound(row);

  return (
    <div className="aecompany">
      {/* What to do next. None borrowed from the strategist when nothing fits. */}
      {proposals.length ? (
        <PanelSection title="Suggested next steps">
          {proposals.map(proposal => (
            <div key={proposal.kind + proposal.title} className="aeproposal">
              <b>{proposal.title}</b>
              <span>{proposal.detail}</span>
            </div>
          ))}
        </PanelSection>
      ) : null}

      {/* The deals, lead deal first. */}
      <PanelSection title={open.length > 1 ? `Open opportunities (${open.length})` : 'Open opportunity'}>
        {lead
          ? [lead, ...open.filter(deal => deal !== lead)].map((deal, index) => (
            <DealCard key={index} deal={deal} today={today} />
          ))
          : <p className="panelempty">No open opportunity on this account.</p>}
      </PanelSection>

      {/* The last touch from each side. The next meeting and the SDR owner are in the strip. */}
      <PanelSection title="Last touches">
        <dl className="aefacts">
          <dt>Last touch (AE)</dt><dd>{dayWithAge(row.last_touch_by_ae, today, 'Nothing logged')}</dd>
          <dt>Last touch (SDR)</dt>
          <dd>
            {dayWithAge(row.last_touch, today, 'Never')}
            {lastSdr?.who ? <span className="muted"> · to {lastSdr.who}</span> : null}
          </dd>
        </dl>
      </PanelSection>

      {touches.length ? (
        <PanelSection title="Recent touches" aside={`latest ${touches.length} · all on History`}>
          {touches.map((touch, index) => <TouchRow key={index} touch={touch} />)}
        </PanelSection>
      ) : null}

      {trends.length ? (
        <PanelSection title="Usage by org" aside="credits, last 30 days vs the 30 before">
          {trends.map(trend => <OrgRow key={trend.org.name} trend={trend} />)}
        </PanelSection>
      ) : null}

      {pastDeals(row).length ? (
        <PanelSection title="Past opportunities">
          {pastDeals(row).map((deal, index) => (
            <div key={index} className="aecontact">
              {deal.name || 'Unnamed opportunity'}
              <span className="muted"> · {dealOutcome(deal)}{deal.close_date ? ` · closed ${deal.close_date}` : ''}</span>
            </div>
          ))}
        </PanelSection>
      ) : null}

      <p className="tabsource">Sources: Salesforce, Gong, product usage.</p>
    </div>
  );
}
